import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, RefreshCw, Inbox, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "new", label: "New", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  { key: "contacted", label: "Contacted", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  { key: "won", label: "Won", cls: "bg-green-100 text-green-700 border-green-200" },
  { key: "lost", label: "Lost", cls: "bg-slate-200 text-slate-600 border-slate-300" },
];

export default function Admin() {
  const { token, user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/estimates`, auth);
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    const prev = items;
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await axios.patch(`${API}/estimates/${id}`, { status }, auth);
      toast.success(`Marked as ${status}`);
    } catch (e) {
      setItems(prev);
      toast.error("Could not update status");
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => { c[s.key] = items.filter((i) => (i.status || "new") === s.key).length; });
    return c;
  }, [items]);

  const visible = filter === "all" ? items : items.filter((i) => (i.status || "new") === filter);

  return (
    <div className="min-h-screen bg-[var(--apex-surface)]">
      <div className="bg-[var(--apex-primary)] text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex items-center justify-between">
          <div>
            <h1 className="font-display font-black text-2xl">Estimate Requests</h1>
            <p className="text-slate-300 text-sm mt-1">{items.length} total · signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-sm bg-white/10 hover:bg-white/20 px-4 py-2.5 text-sm font-semibold transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-sm bg-white/10 hover:bg-white/20 px-4 py-2.5 text-sm font-semibold transition-colors">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`rounded-sm px-4 py-2 text-sm font-semibold border transition-colors ${filter === "all" ? "bg-[var(--apex-primary)] text-white border-[var(--apex-primary)]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
          >
            All <span className="opacity-70">({counts.all})</span>
          </button>
          {STATUSES.map((s) => (
            <button
              key={s.key}
              data-testid={`filter-${s.key}`}
              onClick={() => setFilter(s.key)}
              className={`rounded-sm px-4 py-2 text-sm font-semibold border transition-colors ${filter === s.key ? "bg-[var(--apex-primary)] text-white border-[var(--apex-primary)]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            >
              {s.label} <span className="opacity-70">({counts[s.key]})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : visible.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <Inbox size={40} className="mx-auto mb-3 opacity-50" />
            No requests in this view.
          </div>
        ) : (
          <div className="grid gap-4">
            {visible.map((it) => {
              const current = it.status || "new";
              return (
                <div key={it.id} data-testid="admin-estimate-row" className="bg-white rounded-md border border-slate-200 p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-display font-bold text-lg text-[var(--apex-primary)]">{it.name}</div>
                        <span data-testid="lead-status-badge" className={`rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${STATUSES.find((s) => s.key === current)?.cls}`}>
                          {STATUSES.find((s) => s.key === current)?.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-slate-600">
                        <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 hover:text-[var(--apex-accent)]"><Mail size={14} /> {it.email}</a>
                        <a href={`tel:${it.phone}`} className="flex items-center gap-1.5 hover:text-[var(--apex-accent)]"><Phone size={14} /> {it.phone}</a>
                        {it.address && <span className="flex items-center gap-1.5"><MapPin size={14} /> {it.address}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-block rounded-sm bg-[var(--apex-accent)]/10 text-[var(--apex-accent)] text-xs font-semibold px-2.5 py-1">{it.service_type}</span>
                      <div className="text-xs text-slate-400 mt-1.5">{new Date(it.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100 text-sm">
                    <div><span className="text-slate-400">Budget:</span> <span className="text-slate-700 font-medium">{it.budget || "—"}</span></div>
                    <div><span className="text-slate-400">Timeline:</span> <span className="text-slate-700 font-medium">{it.timeline || "—"}</span></div>
                    <div><span className="text-slate-400">Source:</span> <span className="text-slate-700 font-medium">{it.referral || "—"}</span></div>
                  </div>
                  {(it.project_scope || it.details) && (
                    <div className="mt-3 text-sm text-slate-600 space-y-1">
                      {it.project_scope && <p><span className="text-slate-400">Scope:</span> {it.project_scope}</p>}
                      {it.details && <p><span className="text-slate-400">Details:</span> {it.details}</p>}
                    </div>
                  )}
                  {it.photos && it.photos.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-photos">
                      {it.photos.map((p) => (
                        <a key={p.path} href={`${API}/files/${p.path}`} target="_blank" rel="noreferrer" className="block h-20 w-20 rounded-sm overflow-hidden ring-1 ring-slate-200 hover:ring-[var(--apex-accent)] transition-all">
                          <img src={`${API}/files/${p.path}`} alt={p.filename} className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-1">Set status:</span>
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`set-status-${s.key}-${it.id}`}
                        onClick={() => setStatus(it.id, s.key)}
                        className={`rounded-sm px-3 py-1.5 text-xs font-semibold border transition-colors ${current === s.key ? s.cls : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
